/**
 * NavIcons — crisp inline SVG glyphs for the left rail.
 *
 * Replaces the prior Unicode emoji/symbol set with consistent stroke-style
 * icons aligned to the Aurora design language: 22px viewBox, 1.75px
 * currentColor stroke, no fills (so the active/hover color cascade flows
 * through cleanly). Square line caps + joins for the slightly technical
 * feel of the new Dashboard, with rounded caps reserved for the few
 * organic glyphs (sparkle, beaker, chat).
 */
import type { JSX, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const baseProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 22 22',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export type NavIconKey =
  | 'dashboard'
  | 'zilm'
  | 'funding'
  | 'compliance'
  | 'analytics'
  | 'workflows'
  | 'assessments'
  | 'briefs'
  | 'admin'
  | 'feedback'
  | 'data-explorer'
  | 'data-pane'
  | 'tools'
  | 'labs'
  | 'whatsnew'
  | 'user';

function DashboardIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="12" y="3" width="7" height="4" rx="1.5" />
      <rect x="3" y="12" width="7" height="7" rx="1.5" />
      <rect x="12" y="9" width="7" height="10" rx="1.5" />
    </svg>
  );
}

function ZilmIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M12 2 L5 13 L11 13 L10 20 L17 9 L11 9 Z" />
    </svg>
  );
}

function FundingIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <circle cx="11" cy="11" r="8" />
      <path d="M14 7 H10 a2 2 0 0 0 0 4 h2 a2 2 0 0 1 0 4 H8" />
      <path d="M11 5 V7" />
      <path d="M11 15 V17" />
    </svg>
  );
}

function ComplianceIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M11 2 L4 5 V11 c0 4.5 3 7.5 7 8.5 c4-1 7-4 7-8.5 V5 Z" />
      <path d="M8 11 L10.5 13.5 L14.5 9.5" />
    </svg>
  );
}

function AnalyticsIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M3 19 H19" />
      <rect x="5" y="11" width="3" height="6" rx="0.5" />
      <rect x="10" y="7" width="3" height="10" rx="0.5" />
      <rect x="15" y="4" width="3" height="13" rx="0.5" />
    </svg>
  );
}

function WorkflowsIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="16" cy="6" r="2.5" />
      <circle cx="11" cy="16" r="2.5" />
      <path d="M7 8 L10 14" />
      <path d="M15 8 L12 14" />
    </svg>
  );
}

function AssessmentsIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <rect x="5" y="4" width="12" height="15" rx="2" />
      <path d="M9 4 V3 a1 1 0 0 1 1 -1 h2 a1 1 0 0 1 1 1 v1" />
      <path d="M8 11 L10.5 13.5 L14.5 9" />
      <path d="M8 16 H14" />
    </svg>
  );
}

function BriefsIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M5 3 H13 L17 7 V19 a1 1 0 0 1 -1 1 H6 a1 1 0 0 1 -1 -1 Z" />
      <path d="M13 3 V7 H17" />
      <path d="M8 11 H14" />
      <path d="M8 14 H14" />
      <path d="M8 17 H12" />
    </svg>
  );
}

function AdminIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <circle cx="11" cy="11" r="3" />
      <path d="M11 2 V4 M11 18 V20 M2 11 H4 M18 11 H20 M4.6 4.6 L6 6 M16 16 L17.4 17.4 M4.6 17.4 L6 16 M16 6 L17.4 4.6" />
    </svg>
  );
}

function FeedbackIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M3 5 a2 2 0 0 1 2 -2 H17 a2 2 0 0 1 2 2 V13 a2 2 0 0 1 -2 2 H10 L6 19 V15 H5 a2 2 0 0 1 -2 -2 Z" />
      <circle cx="8" cy="9" r="0.6" fill="currentColor" />
      <circle cx="11" cy="9" r="0.6" fill="currentColor" />
      <circle cx="14" cy="9" r="0.6" fill="currentColor" />
    </svg>
  );
}

function DataExplorerIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <circle cx="5" cy="5" r="2" />
      <circle cx="17" cy="5" r="2" />
      <circle cx="11" cy="11" r="2" />
      <circle cx="5" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
      <path d="M6.5 6.5 L9.5 9.5" />
      <path d="M15.5 6.5 L12.5 9.5" />
      <path d="M6.5 15.5 L9.5 12.5" />
      <path d="M15.5 15.5 L12.5 12.5" />
    </svg>
  );
}

function DataPaneIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <rect x="3" y="4" width="16" height="14" rx="2" />
      <path d="M3 9 H19" />
      <path d="M9 9 V18" />
      <path d="M14 9 V18" />
    </svg>
  );
}

function ToolsIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M14.5 4 a4 4 0 0 1 3.5 5.5 L11 16.5 L7 12.5 L13.5 6 a4 4 0 0 1 1 -2 Z" />
      <path d="M7 12.5 L4.5 15 a1.5 1.5 0 0 0 0 2.1 L4.9 17.5 a1.5 1.5 0 0 0 2.1 0 L9.5 15" />
    </svg>
  );
}

function LabsIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M9 3 H13" />
      <path d="M9 3 V8 L4 17 a2 2 0 0 0 1.7 3 H16.3 a2 2 0 0 0 1.7 -3 L13 8 V3" />
      <path d="M6.5 13 H15.5" />
      <circle cx="9.5" cy="16" r="0.6" fill="currentColor" />
      <circle cx="13" cy="17" r="0.6" fill="currentColor" />
    </svg>
  );
}

function WhatsNewIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <path d="M11 3 L12.4 8.6 L18 10 L12.4 11.4 L11 17 L9.6 11.4 L4 10 L9.6 8.6 Z" />
      <path d="M17 3 L17.7 4.8 L19.5 5.5 L17.7 6.2 L17 8" />
    </svg>
  );
}

function UserIcon(p: IconProps) {
  return (
    <svg {...baseProps} {...p}>
      <circle cx="11" cy="8" r="3.5" />
      <path d="M4 18 C4 14.5 7 12.5 11 12.5 C15 12.5 18 14.5 18 18" />
    </svg>
  );
}

const REGISTRY: Record<NavIconKey, (p: IconProps) => JSX.Element> = {
  dashboard: DashboardIcon,
  zilm: ZilmIcon,
  funding: FundingIcon,
  compliance: ComplianceIcon,
  analytics: AnalyticsIcon,
  workflows: WorkflowsIcon,
  assessments: AssessmentsIcon,
  briefs: BriefsIcon,
  admin: AdminIcon,
  feedback: FeedbackIcon,
  'data-explorer': DataExplorerIcon,
  'data-pane': DataPaneIcon,
  tools: ToolsIcon,
  labs: LabsIcon,
  whatsnew: WhatsNewIcon,
  user: UserIcon,
};

interface Props extends IconProps {
  name: NavIconKey;
}

export function NavIcon({ name, ...rest }: Props): JSX.Element {
  const Cmp = REGISTRY[name];
  return <Cmp {...rest} />;
}
